<!-- ActionButtonV2: Upgrade and slimming of ActionButton -->
<script lang="ts" setup> 
import "../../css/choice.css"
import { ref, computed,nextTick } from 'vue';
import { ActionButtonStyle, DefaultActionButtonValues } from '../lib/Types/Types';
import { IconTypes, DisplayStyle, ButtonDisplaySize,ButtonDisplayStyle } from '../Enums/UiEnums';
import { iconClass, bootstrapButtonClass } from '../lib/StyleHelpers';
import { ICssLength, CssLength, CssLayoutUnits } from '../lib/CssHelpers';
import { showToast } from '../lib/Notification';

  const props = withDefaults(defineProps<{
    action: ActionButtonStyle,
    displayStyle?: DisplayStyle | ButtonDisplayStyle,
    size?: ButtonDisplaySize,
    contextValue?: string | number;
    text?: string,                      // Override default text -- Only use if you don't use the slot
    class?: string,                     // Override default CSS class
    icon?: IconTypes,                   // Override default icon
    disabled?: boolean,
    width?: ICssLength|number|Object,          // Width of the button.  Current in px but should change to em/ex/ch
    tooltip?: string,                   // Tooltip for the button
    iconDisplayStyle?: DisplayStyle,    // Style for action icon
    spinText?: string,                  // Override default spinning text
    spinClass?: string,                 // Override default spinning class
    onBeforeActionValidate?: (action: ActionButtonStyle, contextValue?: string | number) => Promise<boolean>, // Is called before the action to permit validation if needed
    performAction: (contextValue?: string | number) => Promise<void>,    // Action event to perform (show spinner & disable)
    endingPadding?: number            // Add padding to the end of the button
  }>(), {
    disabled: false,
    displayStyle: DisplayStyle.Primary
  });

  defineExpose({actionButtonClick});

  const emits = defineEmits<{
    (e:"click", contextValue?: string | number): void    // KLUDGE: Intercept click event to prevent it from being used if defined on the component
    (e:"performingAction", state?: boolean): void
  }>();
  
  const isExecuting= ref<boolean>(false); 

  const defaultActionButtonValues = computed(() => DefaultActionButtonValues(props.action));
  const computedActionIconClass = computed(() => [defaultActionButtonValues.value.iconClass ?? iconClass(props.icon), "text-center"]);
  const displayActionIcon = computed<boolean>(() => computedActionIconClass.value && !isExecuting.value  ? true : false); 
  const displaySpin = computed<boolean>(() => isExecuting.value ? true : false);
  const computedButtonStyle = computed(() => {
    if (typeof(0) === typeof(props.width))
    {
      return props.width ? `width: ${new CssLength(props.width as number, CssLayoutUnits.px).cssValue};` : undefined
    }
    const cssLength = props.width as ICssLength;
    return props.width ? `width: ${new CssLength(cssLength.amount, cssLength.units).cssValue};` : undefined
  });
  const computedSpinClass = computed(() => defaultActionButtonValues.value.spinClass ?? props.spinClass ?? "fa-solid fa-spinner fa-spin me-1");
  const computedDisabled = computed<boolean>(() => props.disabled ? true : isExecuting.value);

  const computedButtonClass = computed(() => {
    if (isExecuting.value && props.spinClass)
    {
        return props.spinClass;
    }
    if (props.class?.length)
      return props.class;

    return (bootstrapButtonClass(props.displayStyle, props.size) ?? "btn btn-primary")
        + ((props.endingPadding ?? 0) > 0 ? ` me-${props.endingPadding}` : "");
  });

  const buttonDisplayedText = computed<string>(() => {
    const displayed_text = (props.text ?? defaultActionButtonValues.value.baseText ?? props.action.toString() ?? "Button" );
    const spinning_text = (props.spinText ?? defaultActionButtonValues.value.spinText ?? displayed_text); 
    return isExecuting.value ? spinning_text : displayed_text
  }); 

  async function actionButtonClick() {     
    if (undefined === props.performAction)
    {
        alert("performAction not specified");
        return;
    }

    // Check to make sure we can perform the action
    if (props.onBeforeActionValidate)
    {
      if (!await props.onBeforeActionValidate(props.action, props.contextValue))
        return;
    }
    
    isExecuting.value = true; 
    await nextTick();   // Allow UI to update if the call is actually a sync function

    emits("performingAction", true);

    try
    {
      await props.performAction(props.contextValue);
    }
    catch (e)
    {
      showToast(`Error performing action: ${e.message}`, DisplayStyle.Danger);
      console.error("Error performing action in ActionButtonV2: ", e);
    }
    emits("performingAction", false);
    isExecuting.value = false; 
  }
</script>

<template>
  <button :class="computedButtonClass" :disabled="computedDisabled" @click="actionButtonClick" :style="computedButtonStyle" :title="props.tooltip" type="button">    
    <slot v-if="!isExecuting">
      <em :class="computedActionIconClass" :displayStyle="props.iconDisplayStyle" v-if="displayActionIcon" ></em>
      {{buttonDisplayedText}}
    </slot>

    <slot name="executing" v-if="isExecuting">
      <em :class="computedSpinClass"></em>
      {{buttonDisplayedText}} 
    </slot>
  </button>
</template>